import { isPlatformServer } from '@angular/common';
import { httpResource } from '@angular/common/http';
import {
	computed,
	effect,
	inject,
	Injectable,
	linkedSignal,
	makeStateKey,
	PLATFORM_ID,
	Resource,
	resourceFromSnapshots,
	ResourceSnapshot,
	Signal,
	signal,
	StateKey,
	TransferState,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';

import { filter, map, startWith } from 'rxjs';

import { fallbackLanguage } from '@valk-nx/core/lib/core';
import { LanguageSwitchHelper } from '@valk-nx/helpers/lib/language/language-switch';
import type {
	FilterableHotelListData,
	FilterableListItems,
	FilterableListScenario,
	FilterablePackageBatchListData,
} from '@valk-nx/storyblok-types/src/lib/types/filterable-list.service.interface';

import { CONTENT_SERVICE_URL } from './content.service';

export const DEFAULT_DATA: FilterableHotelListData = {
	hotels: [],
	filters: [],
};

export const DEFAULT_PACKAGES_DATA: FilterablePackageBatchListData = {
	packageBatches: [],
	filters: [],
};

type FiltersList = Record<string, string[]>;
type FilterParams = Record<string, string>;

/** Keys of `FilterableListItems` whose value is a string array (e.g. `themeGuids`). */
type ArrayKeys = {
	[K in keyof FilterableListItems]-?: NonNullable<
		FilterableListItems[K]
	> extends string[]
		? K
		: never;
}[keyof FilterableListItems];

/** Keys of `FilterableListItems` whose value is a single string (e.g. `themeGuid`). */
type StringKeys = {
	[K in keyof FilterableListItems]-?: NonNullable<
		FilterableListItems[K]
	> extends string
		? K
		: never;
}[keyof FilterableListItems];

export function withTransferredValue<T>(
	input: Resource<T>,
	stateKey: Signal<StateKey<T> | undefined>,
	transferState: TransferState,
): Resource<T> {
	const derived = linkedSignal<
		{ snapshot: ResourceSnapshot<T>; key: StateKey<T> | undefined },
		ResourceSnapshot<T>
	>({
		source: () => ({ snapshot: input.snapshot(), key: stateKey() }),
		computation: ({ snapshot, key }, previous) => {
			// We intentionally don't remove the key from the transfer state after reading, to avoid refetching data during the same session.
			if (
				snapshot.status === 'idle' &&
				key &&
				transferState.hasKey(key)
			) {
				return {
					status: 'resolved' as const,
					value: transferState.get<T>(key, snapshot.value),
				};
			}
			if (
				snapshot.status === 'loading' &&
				previous &&
				previous.value.status !== 'error'
			) {
				// Keep the last good value visible while the new request loads
				return {
					status: 'loading' as const,
					value: previous.value.value,
				};
			}
			return snapshot;
		},
	});
	return resourceFromSnapshots(derived);
}

function extractLanguageCode(url: string): string | undefined {
	return url.split('/').find(Boolean);
}

@Injectable({ providedIn: 'root' })
export class FilterableListService {
	private readonly serviceUrl = inject(CONTENT_SERVICE_URL, {
		optional: true,
	});
	private readonly router = inject(Router);
	private readonly platformId = inject(PLATFORM_ID);
	private readonly transferState = inject(TransferState);
	readonly language = toSignal(
		this.router.events.pipe(
			filter((e) => e instanceof NavigationEnd),
			startWith(null),
			map(() => {
				const candidate = extractLanguageCode(this.router.url);
				return candidate &&
					LanguageSwitchHelper.isLanguageCode(candidate)
					? candidate
					: fallbackLanguage;
			}),
		),
		{ initialValue: fallbackLanguage },
	);

	filters = signal<FiltersList>({});
	scenario = signal<FilterableListScenario | null>(null);
	listItems = signal<FilterableListItems>({});

	private readonly vdcHotelsRequestPath = computed<string | undefined>(() =>
		this.scenario() === 'vdc-hotels'
			? this.buildRequestPath('vdc/hotels')
			: undefined,
	);

	private readonly vdcvhiHotelsRequestPath = computed<string | undefined>(
		() =>
			this.scenario() === 'vdcvhi-hotels'
				? this.buildRequestPath(
						'valkcom/hotels',
						this.buildStringParams(['themeGuid']),
					)
				: undefined,
	);

	private readonly vdcPackagesRequestPath = computed<string | undefined>(
		() =>
			this.scenario() === 'vdc-packages'
				? this.buildRequestPath(
						'package-batches',
						this.buildGuidParams(['hotelGuids', 'themeGuids']),
					)
				: undefined,
	);

	/** Builds `<path>?<listItem params>&<sorted filters>&language=<lang>`. */
	private buildRequestPath(
		path: string,
		listItemParams: FilterParams = {},
	): string {
		const params: FilterParams = { ...listItemParams };

		const filters = this.filters();
		// Sort keys so the query string (and the cache key derived from it) is
		// deterministic regardless of the order filters were applied.
		const sortedFilterKeys = Object.keys(filters).sort((a, b) =>
			a.localeCompare(b),
		);
		for (const key of sortedFilterKeys) {
			params[key] = filters[key].join(',');
		}

		params['language'] = this.language();

		return `${path}?${new URLSearchParams(params).toString()}`;
	}

	/** Picks the requested guid lists from `listItems`, omitting empty ones. */
	private buildGuidParams(keys: ArrayKeys[]): FilterParams {
		// listItems can be null at runtime when the Storyblok field is empty
		const items = this.listItems() ?? {};
		const params: FilterParams = {};

		for (const key of keys) {
			const guids = items[key] ?? [];
			if (guids.length) {
				params[key] = guids.join(',');
			}
		}

		return params;
	}

	/** Picks the requested single-value string params from `listItems`, omitting empty ones. */
	private buildStringParams(keys: StringKeys[]): FilterParams {
		// listItems can be null at runtime when the Storyblok field is empty
		const items = this.listItems() ?? {};
		const params: FilterParams = {};

		for (const key of keys) {
			const value = items[key];
			// Truthiness is safe here: StringKeys is constrained to string-valued
			// fields, so the only falsy value that can appear is an empty string,
			// which we intentionally treat as "not set".
			if (value) {
				params[key] = value;
			}
		}

		return params;
	}

	readonly vdcHotels: Resource<FilterableHotelListData> =
		this.createTransferStateResource(
			this.vdcHotelsRequestPath,
			DEFAULT_DATA,
		);

	readonly vdcvhiHotels: Resource<FilterableHotelListData> =
		this.createTransferStateResource(
			this.vdcvhiHotelsRequestPath,
			DEFAULT_DATA,
		);

	readonly vdcPackages: Resource<FilterablePackageBatchListData> =
		this.createTransferStateResource(
			this.vdcPackagesRequestPath,
			DEFAULT_PACKAGES_DATA,
		);

	private createTransferStateResource<T>(
		requestPath: Signal<string | undefined>,
		defaultValue: T,
	): Resource<T> {
		const stateKey = computed<StateKey<T> | undefined>(() => {
			const path = requestPath();
			return path
				? makeStateKey<T>(`filterable-list-${path}`)
				: undefined;
		});

		const resource = httpResource<T>(
			() => {
				const path = requestPath();
				if (!path) return undefined;

				const key = stateKey();
				// Skip the request as long as the key is transferred; the
				// key is never removed, so the initial param-set never
				// refetches during the session (see withTransferredValue)
				if (key && this.transferState.hasKey(key)) return undefined;

				return {
					url: `${this.serviceUrl}${path}`,
					method: 'GET',
					transferCache: false,
				};
			},
			{
				defaultValue,
				parse: (raw) => (raw as { data: T }).data,
			},
		);

		effect(() => {
			const key = stateKey();
			if (
				isPlatformServer(this.platformId) &&
				key &&
				resource.status() === 'resolved'
			) {
				this.transferState.set(key, resource.value());
			}
		});

		return withTransferredValue(resource, stateKey, this.transferState);
	}
}
